import {
  Camera,
  CameraResultType,
  CameraSource,
  CameraDirection,
} from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";

export interface PhotoOptions {
  allowCamera?: boolean;
  allowGallery?: boolean;
  quality?: number;
  width?: number;
  height?: number;
}

export interface PhotoResult {
  success: boolean;
  imageUrl?: string;
  error?: string;
}

export interface PhotoSelectionOption {
  label: string;
  value: "camera" | "gallery";
  icon: string;
  description: string;
}

export class CameraService {
  /**
   * Get available photo source options based on platform
   */
  static getPhotoSourceOptions(): PhotoSelectionOption[] {
    const options: PhotoSelectionOption[] = [];

    if (Capacitor.isNativePlatform()) {
      // Native platform - show camera and gallery options
      options.push(
        {
          label: "Take Photo",
          value: "camera",
          icon: "camera",
          description: "Use camera to take a new photo",
        },
        {
          label: "Choose from Gallery",
          value: "gallery",
          icon: "image",
          description: "Select from your photo library",
        },
      );
    } else {
      // Web platform - show file upload option
      options.push({
        label: "Upload Photo",
        value: "gallery",
        icon: "upload",
        description: "Choose a photo from your device",
      });
    }

    return options;
  }

  /**
   * Take photo using device camera
   */
  static async takePhoto(options: PhotoOptions = {}): Promise<PhotoResult> {
    try {
      const image = await Camera.getPhoto({
        quality: options.quality || 90,
        allowEditing: true,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        width: options.width || 1024,
        height: options.height || 1024,
        direction: CameraDirection.Rear,
      });

      if (image.dataUrl) {
        return {
          success: true,
          imageUrl: image.dataUrl,
        };
      } else {
        return {
          success: false,
          error: "Failed to capture photo",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Camera access failed",
      };
    }
  }

  /**
   * Select photo from gallery/photo library
   */
  static async selectFromGallery(
    options: PhotoOptions = {},
  ): Promise<PhotoResult> {
    try {
      const image = await Camera.getPhoto({
        quality: options.quality || 90,
        allowEditing: true,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
        width: options.width || 1024,
        height: options.height || 1024,
      });

      if (image.dataUrl) {
        return {
          success: true,
          imageUrl: image.dataUrl,
        };
      } else {
        return {
          success: false,
          error: "Failed to select photo",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Gallery access failed",
      };
    }
  }

  /**
   * Show photo source selection and handle the chosen option
   */
  static async selectPhotoWithOptions(
    options: PhotoOptions = {},
  ): Promise<PhotoResult> {
    if (Capacitor.isNativePlatform()) {
      // On native platforms, we'll let the component handle the action sheet
      // This method is primarily for direct usage
      return {
        success: false,
        error: "Use selectPhotoSource method for native platforms",
      };
    } else {
      // On web, fallback to file input
      return new Promise((resolve) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/jpeg,image/jpg,image/png,image/webp";

        input.onchange = (event) => {
          const file = (event.target as HTMLInputElement).files?.[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                success: true,
                imageUrl: e.target?.result as string,
              });
            };
            reader.onerror = () => {
              resolve({
                success: false,
                error: "Failed to read file",
              });
            };
            reader.readAsDataURL(file);
          } else {
            resolve({
              success: false,
              error: "No file selected",
            });
          }
        };

        input.click();
      });
    }
  }

  /**
   * Check if  camera and photos permissions are granted
   */
  static async checkCameraAndPhotosPermissions(): Promise<boolean> {
    try {
      const permission = await Camera.checkPermissions();
      return (
        permission.camera === "granted" &&
        (permission.photos === "granted" || permission.photos === "limited")
      );
    } catch (error) {
      return false;
    }
  }

  /**
   * Request camera and photos permissions
   */
  static async requestCameraAndPhotosPermissions(): Promise<boolean> {
    try {
      const permission = await Camera.requestPermissions({
        permissions: ["camera", "photos"],
      });

      return (
        permission.camera === "granted" &&
        (permission.photos === "granted" || permission.photos === "limited")
      );
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if camera permissions are granted
   */
  private static async checkCameraPermissions(): Promise<boolean> {
    try {
      const permission = await Camera.checkPermissions();
      return permission.camera === "granted";
    } catch (error) {
      return false;
    }
  }

  /**
   * Request camera permissions
   */
  static async checkAndRequestCameraPermissions(): Promise<boolean> {
    try {
      if (await CameraService.checkCameraPermissions()) {
        return true;
      }
      const permission = await Camera.requestPermissions({
        permissions: ["camera"],
      });

      return permission.camera === "granted";
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if photos permissions are granted
   */
  private static async checkPhotosPermissions(): Promise<boolean> {
    try {
      const permission = await Camera.checkPermissions();
      return permission.photos === "granted" || permission.photos === "limited";
    } catch (error) {
      return false;
    }
  }

  /**
   * Request photos permissions
   */
  static async checkAndRequestPhotosPermissions(): Promise<boolean> {
    try {
      if (await CameraService.checkPhotosPermissions()) {
        return true;
      }
      const permission = await Camera.requestPermissions({
        permissions: ["photos"],
      });

      return permission.photos === "granted" || permission.photos === "limited";
    } catch (error) {
      return false;
    }
  }

  /**
   * Convert data URL to File object for upload compatibility
   */
  static dataUrlToFile(dataUrl: string, filename: string = "photo.jpg"): File {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  /**
   * Compress image data URL
   */
  static compressImage(
    dataUrl: string,
    quality: number = 0.8,
    maxWidth: number = 1024,
    maxHeight: number = 1024,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        const ratio = Math.min(maxWidth / width, maxHeight / height);

        if (ratio < 1) {
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(compressedDataUrl);
      };

      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = dataUrl;
    });
  }
}

export default CameraService;
